import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useActiveCall } from "@/lib/activeCall";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { useVideoFitMode } from "@/lib/videoFitMode";
import { useLowPowerMode, lowPowerDailyConfigParam } from "@/lib/lowPowerMode";
import { RecordingConsentPanel } from "@/components/canvas/RecordingConsentPanel";
import { isIOS } from "@/utils/browserCapabilities";
import { openExternal } from "@/utils/despia";
import {
  PhoneOff,
  Minimize2,
  Maximize2,
  GripHorizontal,
  ArrowLeft,
  Zap,
  ZapOff,
  X,
} from "lucide-react";


const PIP_SIZES = [
  { label: "S", w: 280, h: 210 },
  { label: "M", w: 360, h: 270 },
  { label: "L", w: 480, h: 360 },
];

export function GlobalVideoCallOverlay() {
  const { activeCall, clearActiveCall } = useActiveCall();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [fitMode] = useVideoFitMode();
  const [lowPower, setLowPower] = useLowPowerMode();
  // Capture low-power mode at the moment the call starts. Changing the
  // iframe `src` mid-call would tear down the connection, so we freeze the
  // value per-call. Toggling now affects the *next* call (a toast tells the
  // user this).
  const [lowPowerAtJoin, setLowPowerAtJoin] = useState<boolean | null>(null);
  useEffect(() => {
    if (activeCall && lowPowerAtJoin === null) setLowPowerAtJoin(lowPower);
    if (!activeCall && lowPowerAtJoin !== null) setLowPowerAtJoin(null);
  }, [activeCall, lowPower, lowPowerAtJoin]);

  // PiP state
  const [minimized, setMinimized] = useState(false);
  const [sizeIndex, setSizeIndex] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [pipInitialized, setPipInitialized] = useState(false);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);

  // Canvas portal rect tracking
  const [portalRect, setPortalRect] = useState<DOMRect | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const rafRef = useRef(0);

  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 1024;

  const isOnCanvas =
    !!activeCall &&
    location.pathname === `/sessions/${activeCall.sessionId}/canvas`;

  // Tracks whether the local participant has actually left the Daily meeting
  // (clicked Leave inside the iframe). A left meeting plus navigating away
  // from the canvas means there is nothing to return to, so we tear the
  // overlay down instead of leaving a stale "Live" pill behind.
  const [leftMeeting, setLeftMeeting] = useState(false);
  const isOnCanvasRef = useRef(isOnCanvas);
  isOnCanvasRef.current = isOnCanvas;

  useEffect(() => {
    if (!activeCall) setLeftMeeting(false);
  }, [activeCall]);

  useEffect(() => {
    if (leftMeeting && !isOnCanvas) clearActiveCall();
  }, [leftMeeting, isOnCanvas, clearActiveCall]);

  // ── iOS camera/mic permission primer ──
  // On iOS (Safari, installed PWA, and the native shell) the camera prompt for
  // a cross-origin iframe is unreliable: the prompt can be swallowed and the
  // call joins with a black tile and no way to retry. Asking for the devices
  // once from the top-level page forces the native permission sheet to appear
  // before Daily needs the tracks. Tracks are released immediately.
  const primedRef = useRef(false);
  useEffect(() => {
    if (!activeCall || primedRef.current) return;
    if (!isIOS) return;
    primedRef.current = true;
    navigator.mediaDevices
      ?.getUserMedia({ video: true, audio: true })
      .then((stream) => stream.getTracks().forEach((t) => t.stop()))
      .catch(() => {
        setPermissionBlocked(true);
      });
  }, [activeCall]);

  const [permissionBlocked, setPermissionBlocked] = useState(false);
  const [helpDismissed, setHelpDismissed] = useState(false);
  useEffect(() => {
    if (!activeCall) {
      primedRef.current = false;
      setPermissionBlocked(false);
      setHelpDismissed(false);
    }
  }, [activeCall]);


  // ── Observe #canvas-video-portal to position the iframe over it ──
  useEffect(() => {
    if (!isOnCanvas) {
      setPortalRect(null);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    const findAndObserve = () => {
      const el = document.getElementById("canvas-video-portal");
      if (!el) {
        rafRef.current = requestAnimationFrame(findAndObserve);
        return;
      }

      const update = () => setPortalRect(el.getBoundingClientRect());
      update();

      const ro = new ResizeObserver(update);
      ro.observe(el);
      observerRef.current = ro;

      // Also update on window resize and scroll (in case of any scroll context)
      window.addEventListener("resize", update);
      window.addEventListener("scroll", update, true);
      return () => {
        ro.disconnect();
        window.removeEventListener("resize", update);
        window.removeEventListener("scroll", update, true);
      };
    };

    const cleanup = findAndObserve();
    return () => {
      cancelAnimationFrame(rafRef.current);
      cleanup?.();
    };
  }, [isOnCanvas]);

  // ── Initialise PiP position (bottom-right, desktop) ──
  useEffect(() => {
    if (isOnCanvas || isMobile || pipInitialized) return;
    const size = PIP_SIZES[sizeIndex];
    setPosition({
      x: window.innerWidth - size.w - 16,
      y: window.innerHeight - size.h - 16,
    });
    setPipInitialized(true);
  }, [isOnCanvas, isMobile, pipInitialized, sizeIndex]);

  // ── End call (coach only) ──
  const handleEndCall = useCallback(async () => {
    if (!activeCall) return;

    if (!activeCall.isCoach) {
      clearActiveCall();
      return;
    }

    const { data, error } = await supabase.functions.invoke("end-video-room", {
      body: { sessionId: activeCall.sessionId },
    });

    if (error || data?.error) {
      toast({
        title: "Could not end call",
        description: error?.message || data?.error || "Please try again.",
        variant: "destructive",
      });
      return;
    }

    clearActiveCall();
    toast({ title: "Call ended" });
  }, [activeCall, clearActiveCall, toast]);

  // ── Listen for Daily "left-meeting" postMessage ──
  useEffect(() => {
    if (!activeCall) return;

    // Daily fires `left-meeting` for ANY disconnect, clean leaves, transient
    // network blips, tab backgrounding (mobile Safari), permission loss, and
    // auto-ejection. Because we use a prebuilt iframe (not a callObject),
    // there's no built-in auto-rejoin. Blindly clearing the call on every
    // left-meeting causes "spontaneous quits" mid-conversation.
    //
    // Strategy: when we receive `left-meeting`, give the iframe a grace
    // window to either auto-rejoin OR for the realtime `ended_at` UPDATE to
    // confirm the call was intentionally ended. Only after the grace window
    // (and only if the room is still active in DB) do we tear down.
    let leaveGraceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleMessage = (e: MessageEvent) => {
      // Only trust messages from the Daily.co call frame
      let trustedOrigin = false;
      try {
        const host = new URL(e.origin).hostname;
        trustedOrigin = host === "daily.co" || host.endsWith(".daily.co");
      } catch {
        trustedOrigin = false;
      }
      if (!trustedOrigin) return;
      if (!e.data || typeof e.data !== "object") return;

      // Hard errors that genuinely terminate the call, clear immediately.
      if (
        e.data.action === "error" &&
        (e.data.errorMsg === "ejected" ||
          e.data.errorMsg === "meeting-full" ||
          e.data.errorMsg === "no-room" ||
          e.data.errorMsg === "room-expired")
      ) {
        clearActiveCall();
        return;
      }

      if (e.data.action === "left-meeting") {
        if (leaveGraceTimer) clearTimeout(leaveGraceTimer);
        // Off the canvas there is no video surface to recover into, so drop
        // the overlay right away instead of waiting out the grace window.
        if (!isOnCanvasRef.current) {
          clearActiveCall();
          return;
        }
        setLeftMeeting(true);

        const sessionId = activeCall.sessionId;
        const roomStillLive = async () => {
          const { data, error } = await supabase
            .from("session_video_rooms")
            .select("id")
            .eq("session_id", sessionId)
            .is("ended_at", null)
            .maybeSingle();
          // On a query error assume live so a network blip doesn't tear down.
          if (error) return true;
          return !!data;
        };

        // Distinguish "the call was ended/left on purpose" from "transient
        // disconnect": check the room immediately. If it's already closed the
        // overlay is useless, so tear it down right away instead of waiting.
        (async () => {
          if (!(await roomStillLive())) {
            clearActiveCall();
            return;
          }
          // Room still open: poll during the grace window so an end that
          // lands a second later is picked up quickly.
          let elapsed = 0;
          const poll = async () => {
            if (!isOnCanvasRef.current) return;
            elapsed += 3000;
            if (!(await roomStillLive())) {
              clearActiveCall();
              return;
            }
            if (elapsed < 12000) {
              leaveGraceTimer = setTimeout(poll, 3000);
            }
          };
          leaveGraceTimer = setTimeout(poll, 3000);
        })();
      } else if (

        e.data.action === "joined-meeting" ||
        e.data.action === "participant-joined"
      ) {
        // Successful (re)join, cancel any pending teardown.
        setLeftMeeting(false);
        if (leaveGraceTimer) {
          clearTimeout(leaveGraceTimer);
          leaveGraceTimer = null;
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      if (leaveGraceTimer) clearTimeout(leaveGraceTimer);
    };
  }, [activeCall, clearActiveCall]);

  // ── PiP drag handlers ──
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: position.x,
        origY: position.y,
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!dragRef.current) return;
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        const size = PIP_SIZES[sizeIndex];
        setPosition({
          x: Math.max(
            0,
            Math.min(window.innerWidth - size.w, dragRef.current.origX + dx)
          ),
          y: Math.max(
            0,
            Math.min(window.innerHeight - size.h, dragRef.current.origY + dy)
          ),
        });
      };

      const handleMouseUp = () => {
        dragRef.current = null;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [position, sizeIndex]
  );

  const cycleSize = useCallback(() => {
    const nextIndex = (sizeIndex + 1) % PIP_SIZES.length;
    const nextSize = PIP_SIZES[nextIndex];
    setPosition((prev) => ({
      x: Math.max(0, Math.min(window.innerWidth - nextSize.w, prev.x)),
      y: Math.max(0, Math.min(window.innerHeight - nextSize.h, prev.y)),
    }));
    setSizeIndex(nextIndex);
  }, [sizeIndex]);

  // ── Early return ──
  if (!activeCall) return null;

  // Coach doesn't need a PiP, the call only renders inside the Canvas.
  // We keep the iframe mounted (hidden 1×1) when the coach navigates
  // away, so returning to the canvas doesn't require a rejoin.
  // Clients keep their PiP so they can still see/hear the call while
  // navigating other pages.
  const isOffCanvas = !isOnCanvas;
  const coachHidden = activeCall.isCoach && isOffCanvas;

  // Enable Daily's prejoin screen so the user clicks "Join meeting" themselves.
  // This guarantees a fresh user-gesture context for getUserMedia, which fixes
  // the "rejoined the call but no camera/mic" issue. Without it, an automated
  // join after navigation can lose the gesture chain and silently fail to
  // attach audio/video tracks.
  // NOTE: background blur / video processing UI is intentionally NOT forced on
  // here. The room property `enable_video_processing_ui` is still enabled, so
  // clients can opt in via Daily's settings menu, but we no longer force the
  // pipeline to initialise on join. The MediaPipe segmentation model behind
  // background blur is the single largest CPU/GPU cost in the call and was
  // the main cause of "high CPU + fading quality" reports.
  const iframeUrl =
    `${activeCall.roomUrl}?t=${activeCall.token}` +
    `&showLeaveButton=false&showFullscreenButton=false&showPrejoinUI=true` +
    lowPowerDailyConfigParam(lowPowerAtJoin ?? lowPower);

  const onToggleLowPower = () => {
    const next = !lowPower;
    setLowPower(next);
    toast({
      title: next ? "Low-power mode on" : "Low-power mode off",
      description:
        (lowPowerAtJoin ?? lowPower) === next
          ? next
            ? "Video capped at 360p / 20 fps for better performance."
            : "Full quality restored."
          : "Leave and rejoin the call for the change to take effect.",
    });
  };

  const handleReturnToSession = () => {
    navigate(`/sessions/${activeCall.sessionId}/canvas`);
  };

  // ────────────────────────────────────────────────────────
  // Minimised pill (off-canvas only)
  // ────────────────────────────────────────────────────────
  // Only show the pill when the floating video window is not visible,
  // otherwise the PiP (same corner, same stacking level) covers it.
  const showPill = (minimized && !isOnCanvas) || coachHidden;
  const minimisedPill = showPill && (
    <div
      className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full bg-card border border-border shadow-lg px-4 py-2
        max-w-[calc(100vw-1rem)] max-lg:bottom-[calc(4.5rem+env(safe-area-inset-bottom))] max-lg:right-2"
    >
      <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
      <span className="text-sm font-medium text-foreground">Live</span>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={onToggleLowPower}
        title={lowPower ? "Low-power mode on (tap to disable)" : "Enable low-power mode"}
      >
        {lowPower ? (
          <Zap className="h-4 w-4 text-accent" />
        ) : (
          <ZapOff className="h-4 w-4" />
        )}
      </Button>
      <Button
        type="button"
        size="sm"
        className="h-8 gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-sm"
        onClick={handleReturnToSession}
        title="Return to session"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="whitespace-nowrap">Return to Session</span>
      </Button>
      {!coachHidden && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => setMinimized(false)}
          title="Expand video"
        >
          <Maximize2 className="h-4 w-4" />
        </Button>
      )}
      {activeCall.isCoach && (
        <Button
          variant="destructive"
          size="icon"
          className="h-8 w-8"
          onClick={handleEndCall}
        >
          <PhoneOff className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  // ────────────────────────────────────────────────────────
  // Wrapper styles: canvas overlay vs PiP
  // ────────────────────────────────────────────────────────
  let wrapperStyle: React.CSSProperties;
  let wrapperClassName: string;

  if (isOnCanvas && portalRect) {
    // Position exactly over the portal target
    wrapperStyle = {
      left: portalRect.left,
      top: portalRect.top,
      width: portalRect.width,
      height: portalRect.height,
    };
    wrapperClassName = "fixed z-40 overflow-hidden";
  } else if (isMobile && !isOnCanvas) {
    // Mobile PiP: full overlay between header and bottom nav
    wrapperStyle = {};
    wrapperClassName =
      "fixed z-50 inset-x-2 top-20 bottom-[calc(5rem+env(safe-area-inset-bottom))] flex flex-col rounded-xl border border-border bg-card shadow-2xl overflow-hidden";
  } else {
    // Desktop PiP
    const size = PIP_SIZES[sizeIndex];
    wrapperStyle = {
      left: position.x,
      top: position.y,
      width: size.w,
      height: size.h,
    };
    wrapperClassName =
      "fixed z-50 flex flex-col rounded-xl border border-border bg-card shadow-2xl overflow-hidden";
  }

  // Hide the wrapper (but keep it in DOM) when minimised OR when a coach
  // is off the canvas, keeping the iframe mounted preserves the Daily
  // connection so returning to the canvas doesn't require rejoining.
  if ((minimized && !isOnCanvas) || coachHidden) {
    wrapperStyle = {
      ...wrapperStyle,
      width: 1,
      height: 1,
      opacity: 0,
      pointerEvents: "none",
      overflow: "hidden",
    };
  }

  const showPipControls = !isOnCanvas && !minimized && !coachHidden;
  const showOnCanvasEndCall = isOnCanvas && activeCall.isCoach;

  return (
    <>
      {minimisedPill}
      <RecordingConsentPanel />

      <div className={wrapperClassName} style={wrapperStyle}>
        {/* ── PiP controls bar (hidden on canvas, kept in DOM for stable tree) ── */}
        <div
          className={
            showPipControls
              ? "flex items-center justify-between px-3 py-1.5 bg-card border-b border-border shrink-0 cursor-grab active:cursor-grabbing select-none"
              : "hidden"
          }
          onMouseDown={!isMobile ? handleMouseDown : undefined}
        >
          <div className="flex items-center gap-2">
            {!isMobile && (
              <GripHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <div className="h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse" />
          </div>
          <div
            className="flex items-center gap-1"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Cycle size (desktop only) */}
            {!isMobile && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={cycleSize}
                title={`Size: ${PIP_SIZES[sizeIndex].label}`}
              >
                <span className="text-[10px] font-bold text-muted-foreground">
                  {PIP_SIZES[sizeIndex].label}
                </span>
              </Button>
            )}
            {/* Low-power mode toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onToggleLowPower}
              title={
                lowPower
                  ? "Low-power mode on, 360p / 20 fps (tap to disable)"
                  : "Enable low-power mode for weaker devices"
              }
            >
              {lowPower ? (
                <Zap className="h-3.5 w-3.5 text-accent" />
              ) : (
                <ZapOff className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </Button>
            {/* Return to session */}
            <Button
              type="button"
              size="sm"
              className="h-7 gap-1 bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-semibold"
              onClick={handleReturnToSession}
              title="Return to session"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              <span className="whitespace-nowrap">Return to Session</span>
            </Button>
            {/* Minimise */}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setMinimized(true)}
            >
              <Minimize2 className="h-3.5 w-3.5" />
            </Button>
            {/* End call (coach only) */}
            {activeCall.isCoach && (
              <Button
                variant="destructive"
                size="icon"
                className="h-7 w-7"
                onClick={handleEndCall}
              >
                <PhoneOff className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* On-canvas end-call button removed, Daily.co provides its own Leave button */}

        {/* ── iOS camera help banner: only after a real permission failure ── */}
        {isIOS && !helpDismissed && permissionBlocked && (
          <div className="shrink-0 flex items-start gap-2 bg-accent/15 border-b border-border px-3 py-2 text-xs text-foreground">
            <div className="flex-1">
              <strong>Camera or microphone blocked.</strong> On iPhone: tap the
              "aA" icon in the address bar, then Website Settings, and set Camera
              and Microphone to Allow. Or open the call in Safari.
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-2 h-7 text-xs"
                onClick={() => openExternal(`${activeCall.roomUrl}?t=${activeCall.token}`)}
              >
                Open call in Safari
              </Button>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => setHelpDismissed(true)}
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {/* ── Persistent iframe, never unmounted across navigation ── */}

        <iframe
          src={iframeUrl}
          allow="camera; microphone; autoplay; display-capture; fullscreen"
          allowFullScreen
          className={isOnCanvas ? "block border-0" : "block min-h-0 w-full flex-1 border-0"}
          style={
            isOnCanvas && fitMode === "fill"
              ? {
                  pointerEvents: dragRef.current ? "none" : "auto",
                  width: "140%",
                  height: "140%",
                  marginLeft: "-20%",
                  marginTop: "-20%",
                }
              : isOnCanvas
              ? {
                  pointerEvents: dragRef.current ? "none" : "auto",
                  width: "100%",
                  height: "100%",
                }
              : {
                  pointerEvents: dragRef.current ? "none" : "auto",
                }
          }
          title="Live Coaching Call"
        />
      </div>
    </>
  );
}
